import type { ResponseAssetFeatureProperties } from '@tsr/shared-types';
import {
  ASSET_CLASS_ICON,
  OC_CATEGORY_ICON,
  OC_CATEGORY_LABEL,
  renderCahsFramedGlyph,
  renderLucideToDataUrl,
} from './iconRegistry';
import type { CahsGlyphKey } from './assets/cahs/index.generated';
import { tintedSvgDataUrl } from './svgIcon';
import fireTruck from './assets/response/fire-truck.svg?raw';
import baseDepot from './assets/response/base-depot.svg?raw';

/** Solid-silhouette response markers (Q.1 Stage 4). Fire stations read as a
 *  fire truck; every aircraft-housing base reads as a hangar so it is never
 *  confused with the airframes stationed there. */
const RESPONSE_SVG: Record<string, string> = {
  'fire-truck': fireTruck,
  'base-depot': baseDepot,
};

export interface AssetStyle {
  /** Lucide (or custom) glyph name resolved through iconRegistry. */
  iconName: string;
  /** When set, the marker is a solid tinted SVG silhouette (Stage 4) keyed here
   *  in RESPONSE_SVG, taking precedence over the Lucide glyph. */
  svgKey?: string;
  /** When set, the MAP marker is the CAHS clean-room asset glyph inside its
   *  category frame, rendered in the ambient register (council PART 2.2 Wave-2),
   *  taking precedence over both `svgKey` and the Lucide glyph. `color` is left as
   *  the semantic colour (the side panels still accent with it); the framed glyph
   *  inks itself with the ambient ink — see {@link assetIcon}. */
  cahsGlyph?: CahsGlyphKey;
  /** CSS hex colour (stroke / fill) — the semantic class colour (panel accent). */
  color: string;
  /** Billboard alpha (1 = opaque). Ambient CAHS asset markers cap at 0.85
   *  (Law 4 / PART 2.3); undefined renders opaque, unchanged. */
  alpha?: number;
  /** Billboard pixel size. */
  size: number;
  /** Human label for the side panel / legend. */
  kind: string;
}

const RED = '#ef4444';
const ORANGE = '#f97316';
const BLUE = '#3b82f6';
const GREEN = '#22c55e';
const PURPLE = '#a855f7';
const CYAN = '#06b6d4';
const YELLOW = '#eab308';
const TEAL = '#2dd4bf';
const GOLD = '#facc15';
const AMBER = '#fbbf24';
const SLATE = '#94a3b8';

/* ─────────────── CAHS framed asset glyph (Wave-2, council PART 2.2/2.3) ─────────
 *  Response assets adopt the CAHS glyph WITH its category frame (▭ infrastructure /
 *  ○ operations), rendered in the AMBIENT register: a desaturated ink on a fixed
 *  neutral chip backing, ≤0.85 α, STATIC, never red, never pulsing (PART 2.3). The
 *  frame is a SECOND monochrome encoder — category reads with zero colour — so the
 *  marker no longer leans on hue: the map ink is the same ambient grey for every
 *  asset, and the side-panel keeps the semantic class colour. Clean-room glyphs;
 *  the real Crown symbols swap into the same seam later (#307, legal-gated).
 * ──────────────────────────────────────────────────────────────────────────────*/

/** Ambient glyph + frame-outline ink on the dark globe (PART 2.3: S ≤ 45%). */
export const CAHS_ASSET_AMBIENT_INK = '#a7b0bd';
/** Fixed neutral chip backing behind the framed glyph (PART 2.3) — never a token. */
export const CAHS_ASSET_FRAME_BACKING = '#2b3340';
/** Ambient-register billboard alpha cap (PART 2.3 — point markers ≤ 0.85 α). */
export const CAHS_ASSET_AMBIENT_ALPHA = 0.85;
/** Uniform framed-asset billboard size — frame + inset glyph need the room to read,
 *  and equal-weight ambient chips read as one context layer. */
export const CAHS_ASSET_FRAMED_SIZE = 22;

/**
 * The CAHS asset glyph for an Ontario Corps capability category — the
 * `illustrative_marker` path. EM-native markers we lacked a clean Lucide for lead
 * (council PART 8): the EOC, the staging area, the assembly area. Categories absent
 * here keep their existing Lucide glyph. (#412 swapped these to the real Crown CAHS
 * symbols — the assembly-area glyph is the real CAHS "Emergency Team".)
 */
const OC_CATEGORY_CAHS_GLYPH: Record<string, CahsGlyphKey> = {
  disaster_response: 'emergency-ops-centre', // the disaster-response coordination node
  supplies_logistics: 'staging-area', // logistics marshalling / staging
  shelter_welfare: 'assembly-area', // assembly / welfare gathering point (CAHS "Emergency Team")
  medical_response: 'hospital', // re-skin (was the stethoscope glyph)
  communications: 'comms-tower', // re-skin (was the radio glyph)
};

/**
 * Map a response-asset to its CAHS clean-room asset glyph, or null when CAHS has no
 * asset glyph for it (it then keeps its existing Lucide / silhouette marker). The
 * category FRAME (▭ infrastructure / ○ operations) is derived downstream from the
 * glyph's own category metadata (iconRegistry CATEGORY_FRAME), so this map names
 * only the glyph.
 */
function cahsAssetGlyph(p: ResponseAssetFeatureProperties): CahsGlyphKey | null {
  if (p.asset_class === 'fire_station') return 'fire-station'; // re-skin (was the fire-truck silhouette)
  if (p.asset_class === 'medical_facility') return 'hospital'; // re-skin (was the stethoscope glyph)
  if (p.asset_class === 'illustrative_marker' && p.oc_category) {
    return OC_CATEGORY_CAHS_GLYPH[p.oc_category] ?? null;
  }
  return null;
}

/**
 * Zoom-tier `far` distance (metres) per asset_class (V.3 §7): the eye must be
 * closer than this for the marker to render, so the country view stays clean
 * and local operational detail blooms in on descent. Classes absent here are
 * unbounded (always visible) — aviation bases, wildfire bases, live aircraft.
 */
const ASSET_FAR_M: Record<string, number> = {
  fire_station: 200_000,
  medical_facility: 200_000,
  marine_vessel: 100_000,
  umbrella_org: 100_000,
  volunteer_chapter: 100_000,
  utility_response: 100_000,
  specialty_team: 100_000,
  illustrative_marker: 50_000,
};

/** `far` metres for an asset_class, or null when the marker is unbounded. */
export function assetDistanceFar(assetClass: string | null): number | null {
  if (!assetClass) return null;
  return ASSET_FAR_M[assetClass] ?? null;
}

/**
 * Map a response-asset feature to a marker style. Pure — glyph/colour are
 * driven by asset_class, refined by response_role for the aviation_base class
 * (SAR / medevac / ISR / tanker) and by oc_category for illustrative markers.
 *
 * A keyed class/category additionally gets the CAHS clean-room asset glyph + frame
 * in the ambient register ({@link cahsAssetGlyph}): `cahsGlyph` overrides the MAP
 * marker (rendered with the ambient ink at ≤0.85 α), while `color` stays the
 * semantic class colour for the side-panel accent.
 */
export function classifyAsset(p: ResponseAssetFeatureProperties): AssetStyle {
  const base = classifyAssetBase(p);
  const glyph = cahsAssetGlyph(p);
  if (!glyph) return base;
  return { ...base, cahsGlyph: glyph, alpha: CAHS_ASSET_AMBIENT_ALPHA, size: CAHS_ASSET_FRAMED_SIZE };
}

/** The legacy asset_class/role → Lucide/silhouette style (pre-CAHS-Wave-2). The
 *  semantic colour + label here are preserved on the returned style even when a
 *  CAHS framed glyph overrides the map marker. */
function classifyAssetBase(p: ResponseAssetFeatureProperties): AssetStyle {
  const role = p.response_role;
  switch (p.asset_class) {
    case 'fire_station':
      // Solid fire-truck silhouette (Stage 4) — was a generic building glyph.
      return { iconName: ASSET_CLASS_ICON.fire_station!, svgKey: 'fire-truck', color: RED, size: 20, kind: 'Fire station' };
    case 'wildfire_base':
      // Tanker base = a hangar (houses water bombers); distinct from the airframe.
      return { iconName: ASSET_CLASS_ICON.wildfire_base!, svgKey: 'base-depot', color: ORANGE, size: 22, kind: 'Wildfire / tanker base' };
    case 'aviation_base': {
      // Every aviation base renders as a hangar so it never looks like the
      // aircraft stationed there; role still drives colour + label (Stage 4).
      const base = { iconName: ASSET_CLASS_ICON.aviation_base!, svgKey: 'base-depot', size: 22 };
      if (role === 'medical_transport')
        return { ...base, color: BLUE, kind: 'Air ambulance base' };
      if (role === 'reconnaissance')
        return { ...base, color: PURPLE, kind: 'ISR base' };
      if (role === 'water_drop')
        return { ...base, color: ORANGE, kind: 'Air tanker base' };
      return { ...base, color: GREEN, kind: 'SAR / aviation base' };
    }
    case 'marine_vessel':
      return { iconName: ASSET_CLASS_ICON.marine_vessel!, color: CYAN, size: 20, kind: 'Marine vessel' };
    case 'medical_facility':
      return { iconName: ASSET_CLASS_ICON.medical_facility!, color: BLUE, size: 18, kind: 'Medical facility' };
    case 'volunteer_chapter':
      return { iconName: ASSET_CLASS_ICON.volunteer_chapter!, color: PURPLE, size: 18, kind: 'Volunteer chapter' };
    case 'utility_response':
      return { iconName: ASSET_CLASS_ICON.utility_response!, color: YELLOW, size: 18, kind: 'Utility response' };
    case 'specialty_team':
      return { iconName: ASSET_CLASS_ICON.specialty_team!, color: TEAL, size: 18, kind: 'Specialty team' };
    case 'umbrella_org':
      // The program itself (no parent) is a large gold marker; partner orgs are
      // smaller amber. Both use the `building` glyph (§5).
      return p.parent_org_id === null
        ? { iconName: ASSET_CLASS_ICON.umbrella_org!, color: GOLD, size: 30, kind: 'Umbrella program' }
        : { iconName: ASSET_CLASS_ICON.umbrella_org!, color: AMBER, size: 22, kind: 'Partner organization' };
    case 'illustrative_marker': {
      // Glyph + label come from the Ontario Corps capability category (§4.3);
      // fall back to a plain circle when uncategorised.
      const cat = p.oc_category;
      const iconName = (cat && OC_CATEGORY_ICON[cat]) || ASSET_CLASS_ICON.illustrative_marker!;
      const kind = (cat && OC_CATEGORY_LABEL[cat]) || 'Illustrative location';
      return { iconName, color: AMBER, size: 16, kind };
    }
    default:
      return { iconName: 'circle', color: SLATE, size: 14, kind: 'Response asset' };
  }
}

/**
 * Build (and cache) a data-URI icon for a style. Precedence:
 *   1. CAHS framed asset glyph (Wave-2) — the ambient-register glyph + category
 *      frame, inked the ambient grey (not the semantic colour) and ridden through
 *      the iconRegistry `cahs:` retint path so the #308 mono seam applies. Canvas-
 *      free, so it always renders (never the null fallback).
 *   2. Solid-silhouette styles (fire truck / base hangar) tint their SVG.
 *   3. Everything else rasterises a Lucide glyph via iconRegistry.
 * Returns null only when a Lucide glyph can't be rendered (e.g. no canvas under
 * unit tests) — callers fall back to a coloured point.
 */
export function assetIcon(style: AssetStyle): string | null {
  if (style.cahsGlyph) {
    return renderCahsFramedGlyph(style.cahsGlyph, {
      color: CAHS_ASSET_AMBIENT_INK,
      backing: CAHS_ASSET_FRAME_BACKING,
    });
  }
  if (style.svgKey) {
    const raw = RESPONSE_SVG[style.svgKey];
    if (raw) return tintedSvgDataUrl(style.svgKey, raw, style.color);
  }
  return renderLucideToDataUrl(style.iconName, { color: style.color, size: style.size });
}
